import React, { Suspense, useEffect, useState } from 'react';
import { Button, Tooltip } from 'antd';
import { HelpCircle } from 'lucide-react';

import {
  OPEN_SUPPORT_EVENT,
  type OpenSupportWidgetDetail,
} from './openSupportWidget';

/**
 * O modal só existe depois do primeiro gesto — e a fronteira só vale com o
 * `jaAbriu` abaixo.
 *
 * MEDIDO 2026-09-07: `SupportWidgetModal` (37 kB de fonte) vinha na
 * primeira pintura, porque este componente mora no `AppLayout`, que é o
 * esqueleto de TODA tela autenticada. Era a terceira porta da mesma classe
 * (as duas primeiras: `MessageContent` do Eka e `SupportAssistantMarkdown`).
 *
 * `React.lazy` sozinho NÃO bastaria: o elemento era renderizado sempre, com
 * `open={false}`, então o `import()` dispararia no primeiro render e a
 * fronteira seria decorativa. É o `jaAbriu` que a torna real.
 */
const SupportWidgetModal = React.lazy(() => import('./SupportWidgetModal'));

const SupportWidget: React.FC = () => {
  const [open, setOpen] = useState(false);
  // One-shot pre-fill payload for the ticket form. Set when another surface
  // opens the widget via `openSupportWidget({...})` (e.g. the billing
  // downgrade flows). Consumed + cleared by the modal once applied.
  const [prefill, setPrefill] = useState<OpenSupportWidgetDetail | null>(null);
  // Uma vez aberto, FICA montado: fechar volta a `open={false}` e o antd
  // anima a saída, exatamente como antes. Desmontar ao fechar perderia o
  // rascunho do chamado que a pessoa tivesse digitado.
  const [jaAbriu, setJaAbriu] = useState(false);

  // Allow other surfaces (e.g. the dunning banner on AdminBillingPage, or
  // the billing downgrade flows) to open the support modal without coupling
  // to this component's local state. They dispatch
  // `window.dispatchEvent(new CustomEvent('janus:open-support', { detail }))`
  // — or call the `openSupportWidget()` helper — and we react here. The
  // optional `detail` payload pre-fills the ticket form.
  useEffect(() => {
    const handler = (event: Event) => {
      const detail = (event as CustomEvent<OpenSupportWidgetDetail>).detail;
      setPrefill(detail ?? null);
      setJaAbriu(true);
      setOpen(true);
    };
    window.addEventListener(OPEN_SUPPORT_EVENT, handler);
    return () => window.removeEventListener(OPEN_SUPPORT_EVENT, handler);
  }, []);

  return (
    <>
      <Tooltip title="Suporte" placement="left">
        <Button
          type="primary"
          shape="circle"
          size="large"
          aria-label="Suporte"
          icon={<HelpCircle size={22} />}
          onClick={() => {
            setPrefill(null);
            setJaAbriu(true);
            setOpen(true);
          }}
          className="janus-support-widget-floating"
          style={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            zIndex: 1000,
            width: 52,
            height: 52,
            background: 'linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)',
            border: 'none',
            boxShadow: '0 4px 20px rgba(99,102,241,0.4)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        />
      </Tooltip>

      {/* `fallback={null}` porque o que está carregando é um DIÁLOGO: um
          esqueleto flutuando no canto da tela seria ruído sobre a tela que a
          pessoa estava usando. O botão continua clicável o tempo todo. */}
      {jaAbriu && (
        <Suspense fallback={null}>
          <SupportWidgetModal
            open={open}
            onClose={() => setOpen(false)}
            prefill={prefill}
            onPrefillConsumed={() => setPrefill(null)}
          />
        </Suspense>
      )}
    </>
  );
};

export default SupportWidget;
